/**
 * Utilities for safe cookie handling
 */

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if cookie not found
 */
export const getCookie = (name) => {
  const cookies = document.cookie.split(';');
  const cookie = cookies.find((c) => c.trim().startsWith(`${name}=`));
  
  if (!cookie) return null;
  
  return decodeURIComponent(cookie.split('=')[1]);
};

/**
 * Set cookie with secure parameters
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options
 * @param {string} options.path - Cookie path (default '/')
 * @param {boolean} options.secure - Secure cookie flag (default true)
 * @param {boolean} options.httpOnly - HttpOnly cookie flag (default true)
 * @param {string} options.sameSite - SameSite setting (default 'strict')
 * @param {number} options.maxAge - Cookie lifetime in seconds (default 3600 - 1 hour)
 */
export const setCookie = (name, value, options = {}) => {
  const defaultOptions = {
    path: '/',
    secure: true,
    httpOnly: true, // Adding httpOnly by default
    sameSite: 'strict',
    maxAge: 3600
  };
  
  const opts = { ...defaultOptions, ...options };
  
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  
  // Add options to cookie string
  if (opts.path) cookieString += `; path=${opts.path}`;
  if (opts.secure) cookieString += '; secure';
  if (opts.httpOnly) cookieString += '; httpOnly';
  if (opts.sameSite) cookieString += `; samesite=${opts.sameSite}`;
  if (opts.maxAge) cookieString += `; max-age=${opts.maxAge}`;
  
  // Note: httpOnly flag can only be set by the server, not by client-side JavaScript
  // We include it here for documentation, but it will only work when applied server-side
  document.cookie = cookieString;
};

/**
 * Delete cookie
 * @param {string} name - Cookie name
 */
export const removeCookie = (name) => {
  setCookie(name, '', { maxAge: -1 });
};

/**
 * Check browser cookie support
 * @returns {boolean} Whether cookies are supported
 */
export const areCookiesEnabled = () => {
  try {
    // Test cookie name
    const testKey = '__cookie_test__';

    // Try to set cookie
    document.cookie = `${testKey}=1; path=/; max-age=10`;

    // Check if cookie was set
    const cookieEnabled = document.cookie.indexOf(testKey) !== -1;

    // Remove test cookie
    document.cookie = `${testKey}=; path=/; max-age=-1`;

    return cookieEnabled;
  } catch (e) {
    // In case of error (e.g., in iframe without cookie access)
    console.warn('Cookie check failed:', e);
    return false;
  }
};
